// Quick-join QR rendering (Aug 2026). QR encoding — Reed-Solomon error correction, version/
// format tables — is exactly the kind of well-standardized algorithm worth using a
// battle-tested library for rather than hand-rolling: `qrcode-generator` (MIT, Kazuhiko
// Arase, ~2M weekly npm downloads) is that library. This module is the ONLY place it's
// imported, so if it's ever swapped out later, this is the single file that changes.
import qrcodegen from "qrcode-generator";
import { publicOrigin } from "./buildFlags.js";

// Renders `text` as a QR code data: URL (a PNG, base64-inlined) — a plain <img src=...> can
// render it with no dangerouslySetInnerHTML/innerHTML surface anywhere in the app. Auto
// error-correction level M (15% — a reasonable middle ground for something displayed on a
// screen and scanned at close range, not printed and weathered) and auto type/version
// (0 = smallest QR that fits the data).
export function joinQrDataUrl(text, { cellSize = 6, margin = 2 } = {}) {
  const qr = qrcodegen(0, "M");
  qr.addData(text);
  qr.make();
  return qr.createDataURL(cellSize, margin);
}

// Builds the deep-link URL a QR encodes: this app's own origin with `code` + `jointoken`
// query params, so ANY phone's native camera app can scan it straight into a browser tab —
// no in-app camera/scanner code needed on the joining device, and (unlike an in-app
// scanner) this works identically on iOS Safari, which has no BarcodeDetector API at all.
// LB-341: the one-time ticket's link, which is what "Add a device" builds now. Same shape as
// the join deep link below so qrscan.js and Root's boot read it the same way, but it carries a
// TICKET rather than the standing join token — and a ticket only ever earns a pending request
// that the issuing adult has to approve. The path form is what F2 shows a person
// ("littlebankers.uk/join/7KD4-M2QP"); the query form is what the QR encodes, because the boot
// path already parses query params and a second parser is a second thing to get wrong.
export function joinTicketLink(code, ticket) {
  const origin = publicOrigin();
  const params = new URLSearchParams({ code, jointicket: ticket });
  return `${origin}/?${params.toString()}`;
}

// The short, readable form for F2, and a working URL: GET /join/<code> resolves the ticket to
// its household and redirects into the query form above (worker/handlers/join-link.js). It
// said "not a working URL on its own" here for a week while nothing served that path, which
// is how a person who typed or tapped it landed on the code form instead.
export function joinTicketDisplayLink(display) {
  const origin = publicOrigin();
  return `${origin.replace(/^https?:\/\//, "")}/join/${display}`;
}

export function joinDeepLink(code, joinToken) {
  const origin = publicOrigin();
  const params = new URLSearchParams({ code, jointoken: joinToken });
  return `${origin}/?${params.toString()}`;
}
